import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, AUTH_CONFIG } from '../config';
import { DEFAULT_FETCH_OPTIONS } from '../api-config';
import {
  adminLogin,
  isAdminAuthenticated,
  clearAuthTokens,
  getAdminToken,
  getAuthHeaders,
  storeAdminToken,
  setInMemoryToken
} from '../utils/auth';

// Define authenticated fetch options
const AUTHENTICATED_FETCH_OPTIONS = {
  ...DEFAULT_FETCH_OPTIONS,
  credentials: 'include'
};

// Define a safe fallback for auth endpoints
const safeAuth = {
  currentUser: '/auth/user',
  samlInitiate: '/auth/saml/initiate',
  samlCallback: '/auth/saml/callback',
  manualLogin: '/auth/manual-login',
  logout: '/auth/logout'
};

// Create a default context value to prevent "undefined" errors
const defaultContextValue = {
  currentUser: null,
  loading: true,
  error: null,
  samlConfig: null,
  initiateLogin: () => {},
  handleSAMLCallback: () => Promise.resolve(false),
  manualLogin: () => Promise.resolve(false),
  logout: () => Promise.resolve(false),
  fetchCurrentUser: () => Promise.resolve(),
  fetchSamlConfig: () => Promise.resolve(),
  isAdmin: () => false,
  isSysAdmin: () => false,
  isPowerUser: () => false,
  isSuperAdmin: () => false,
  hasRole: () => false,
  hasAnyRole: () => false,
  canViewInventory: () => false,
  canViewFinancials: () => false,
  canViewSensitiveInventory: () => false,
  canViewCompliance: () => false,
  isUser: () => false,
  handleUnauthorized: () => {}
};

const AuthContext = createContext(defaultContextValue);

export function useAuth() {
  const context = useContext(AuthContext);
  
  // Return a default object with empty functions if context is undefined
  if (!context) {
    console.warn('useAuth() called outside of AuthProvider context or before context was initialized');
    return defaultContextValue;
  }
  
  return context;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [samlConfig, setSamlConfig] = useState(null);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  // Enhanced setCurrentUser that persists to localStorage
  const setAndPersistUser = (userData) => {
    setCurrentUser((prev) => {
      if (!userData) return null;
      // eslint-disable-next-line no-unused-vars
      const { token: _t1, sessionToken: _s1, adminToken: _a1, ...nextSafe } = userData;
      // eslint-disable-next-line no-unused-vars
      const { token: _t0, sessionToken: _s0, adminToken: _a0, ...prevSafe } = prev || {};
      if (prev && JSON.stringify(prevSafe) === JSON.stringify(nextSafe)) {
        return prev;
      }
      return userData;
    });
    if (userData) {
      // Strip session token before persisting — the HttpOnly cookie carries the session.
      // Storing the raw token in localStorage would expose it to XSS.
      // eslint-disable-next-line no-unused-vars
      const { token, sessionToken, adminToken, ...safeUserData } = userData;
      localStorage.setItem('auth_user', JSON.stringify(safeUserData));
      localStorage.setItem('auth_timestamp', Date.now().toString());
    } else {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_timestamp');
    }
  };

  // Check if stored session is still valid (24 hours)
  const isStoredSessionValid = () => {
    const storedUser = localStorage.getItem('auth_user');
    const storedTimestamp = localStorage.getItem('auth_timestamp');
    
    if (!storedUser || !storedTimestamp) {
      return false;
    }
    
    const sessionAge = Date.now() - parseInt(storedTimestamp);
    const maxSessionAge = 24 * 60 * 60 * 1000; // 24 hours
    
    return sessionAge < maxSessionAge;
  };

  // Restore user from localStorage on app start
  const restoreUserFromStorage = () => {
    try {
      if (isStoredSessionValid()) {
        const storedUser = localStorage.getItem('auth_user');
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          // user restored from localStorage
          setCurrentUser(userData);
          return userData;
        }
      } else {
        // Clear expired session
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_timestamp');
      }
    } catch (error) {
      console.error('Error restoring user from storage:', error);
      localStorage.removeItem('auth_user');
      localStorage.removeItem('auth_timestamp');
    }
    return null;
  };

  useEffect(() => {
    console.log('AuthProvider initializing...');
    
    const initAuth = async () => {
      try {
        // Check for SAML callback redirect — the backend redirects to the
        // frontend with ?saml_session=TOKEN&user=JSON after a successful SSO login.
        const params = new URLSearchParams(window.location.search);
        const samlSession = params.get('saml_session');
        const samlUser = params.get('user');
        if (samlSession && samlUser) {
          try {
            setInMemoryToken(samlSession);
            const userData = JSON.parse(decodeURIComponent(samlUser));
            setAndPersistUser(userData);
            // Remove the query params from the URL without a page reload
            window.history.replaceState({}, '', window.location.pathname);
            setLoading(false);
            console.log('Auth initialization complete - SAML session restored');
            return;
          } catch (e) {
            console.error('Error parsing SAML redirect params:', e);
          }
        }

        // First, try to restore user from localStorage
        const restoredUser = restoreUserFromStorage();

        if (restoredUser) {
          const restoredToken = getAdminToken();
          if (restoredToken) {
            // Token found — restore in-memory token so API calls work immediately.
            setInMemoryToken(restoredToken);
            setLoading(false);
            console.log('Auth initialization complete - user restored from storage');
            // Refresh roles/profile from server in background so newly attached roles take effect without re-login.
            fetchCurrentUser();
            return;
          }
          // User metadata exists but session token is gone (e.g. logged in under
          // old code that only used sessionStorage, then opened a new tab).
          // Clear stale user data and fall through to show the login page.
          console.log('Stored user has no session token — clearing and re-authenticating');
          clearAuthTokens();
          localStorage.removeItem('auth_user');
          localStorage.removeItem('auth_timestamp');
          setCurrentUser(null);
          // Skip fetchCurrentUser (we know there's no session) and just load
          // the SAML config so the login page can show the SSO button.
          if (AUTH_CONFIG.samlEnabled) {
            await fetchSamlConfig();
          }
          setLoading(false);
          console.log('Auth initialization complete');
          return;
        }

        // No stored session — just load SAML config (if enabled) so the login
        // page can show the SSO button. No point calling /auth/user here; with
        // no token the backend will always return 401.
        if (AUTH_CONFIG.samlEnabled) {
          await fetchSamlConfig();
        }
        setLoading(false);
        console.log('Auth initialization complete');
      } catch (error) {
        console.error('Error during auth initialization:', error);
        setError('Failed to initialize authentication');
      }
    };
    
    initAuth();

    // Background session check — verify session every 15 minutes
    const sessionInterval = setInterval(() => {
      if (isAdminAuthenticated()) {
        console.log('Running background session check...');
        fetchCurrentUser();
      }
    }, 15 * 60 * 1000);

    // Listen for storage changes (logout in another tab)
    const handleStorageChange = (e) => {
      if (e.key === 'auth_user' && e.newValue === null) {
        // User logged out in another tab
        console.log('User logged out in another tab');
        setCurrentUser(null);
        navigateRef.current('/');
      } else if (e.key === 'auth_user' && e.newValue) {
        // User logged in in another tab
        try {
          const userData = JSON.parse(e.newValue);
          console.log('User logged in in another tab');
          setCurrentUser(userData);
        } catch (error) {
          console.error('Error parsing user data from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Global 401 interceptor — any fetch to our API that returns 401 means
    // the session has expired (or never existed). Force the user back to the
    // login page so they can re-auth via SAML, regardless of which page
    // triggered the call. Runs once at mount, restored on unmount.
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch.apply(window, args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
        const isOurApi = url.startsWith(API_BASE_URL) || url.startsWith('/api/') || url.startsWith('/auth/');
        if (response.status === 401 && isOurApi) {
          // Don't redirect if we're already on the login page or the call is
          // the login-time SAML config probe (expected to be unauth on cold open).
          const onLoginPage = window.location.pathname === '/' || window.location.pathname === '/manual-login';
          const isLoginProbe = url.includes('/admin/saml-config') || url.includes('/auth/saml/') || url.includes('/admin/branding');
          if (!onLoginPage && !isLoginProbe) {
            console.warn(`Session expired (401 from ${url}), redirecting to login`);
            clearAuthTokens();
            try {
              localStorage.removeItem('auth_user');
              localStorage.removeItem('auth_timestamp');
            } catch (_) { /* storage unavailable */ }
            window.location.href = '/';
          }
        }
      } catch (_) { /* never let the interceptor break the response */ }
      return response;
    };

    // Cleanup function
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(sessionInterval);
      window.fetch = originalFetch;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}${safeAuth.currentUser}`, {
        ...AUTHENTICATED_FETCH_OPTIONS,
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const userData = await response.json();
        setAndPersistUser(userData);
      } else if (response.status === 401 || response.status === 403) {
        // Only treat as session expiry if we had an active session
        if (currentUser) {
          console.log('Session expired (401/403), redirecting to login...');
          handleUnauthorized();
        }
      }
    } catch (err) {
      console.error('Error fetching current user:', err);
      // We don't automatically redirect on network errors, only on auth errors
      // setError('Failed to fetch user information');
    } finally {
      setLoading(false);
    }
  };

  const fetchSamlConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/saml-config`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      if (response.ok) {
        const config = await response.json();
        setSamlConfig(config);
      }
    } catch (err) {
      console.error('Error fetching SAML config:', err);
      setSamlConfig(null);
    }
  };

  const initiateLogin = () => {
    window.location.href = `${API_BASE_URL}${safeAuth.samlInitiate}`;
  };

  const manualLogin = async (username, password) => {
    setLoading(true);
    setError(null);
    
    try {
      // Check for admin login
      if (username === 'admin') {
        const loginPassword = password;

        const result = await adminLogin(loginPassword);

        if (result.success) {
          const adminUser = result.user || {
            id: 'admin-1',
            username: 'admin',
            email: 'admin@cloudcor.cloud',
            name: 'System Administrator',
            roles: ['SuperAdmin', 'Admin', 'PowerUser', 'ReadOnly'],
            isAdmin: true
          };
          
          setAndPersistUser(adminUser);
          // Return the user so callers can redirect without waiting for React state
          return { success: true, user: adminUser };
        } else {
          setError(result.error || 'Invalid admin credentials');
          return { success: false };
        }
      }

      // Regular user login
      const response = await fetch(`${API_BASE_URL}${safeAuth.manualLogin}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password }),
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        setAndPersistUser(userData);
        return { success: true, user: userData };
      } else {
        setError('Invalid username or password');
        return { success: false };
      }
    } catch (err) {
      console.error('Error during login:', err);
      setError('Login failed. Please try again later.');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const handleSAMLCallback = async (samlResponse) => {
    // No implementation needed for now - handled by backend
    return false;
  };

  const logout = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Clear authentication tokens
      clearAuthTokens();
      
      // Clear local state
      setCurrentUser(null);
      setSamlConfig(null);
      
      // Clear persisted user data
      localStorage.removeItem('currentUser');
      
      // Try to call logout endpoint (optional)
      try {
        await fetch(`${API_BASE_URL}${safeAuth.logout}`, {
          method: 'POST',
          credentials: 'include'
        });
      } catch (logoutError) {
        // Logout endpoint error is not critical
        console.warn('Logout endpoint error:', logoutError);
      }
      
      return true;
    } catch (err) {
      console.error('Error during logout:', err);
      setError('Logout failed. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Clears session and redirects to login.  Call this from any component that
  // receives a 401/403 from the API — indicates the session has expired.
  const handleUnauthorized = useCallback(() => {
    clearAuthTokens();
    setAndPersistUser(null);
    navigate('/');
  }, [navigate]);

  // Role check functions
  const hasRole = (role) => {
    return currentUser?.roles?.includes(role) || false;
  };

  const hasAnyRole = (roles = []) => {
    return roles.some((role) => hasRole(role));
  };

  const isAdmin = () => {
    return hasRole('Admin') || hasRole('SuperAdmin') || hasRole('SysAdmin');
  };

  const isSysAdmin = () => {
    return hasRole('SuperAdmin') || hasRole('SysAdmin');
  };

  const isPowerUser = () => {
    return hasRole('PowerUser') || hasRole('Admin') || hasRole('SuperAdmin') || hasRole('SysAdmin');
  };

  const isSuperAdmin = () => {
    return hasRole('SuperAdmin') || hasRole('SysAdmin');
  };

  const isUser = () => {
    return hasRole('User') || hasRole('ReadOnly');
  };

  const canViewFinancials = () => {
    return hasAnyRole(['User', 'ReadOnly', 'PowerUser', 'Admin', 'SuperAdmin', 'SysAdmin', 'FinancialsViewer']);
  };

  const canViewInventory = () => {
    return canViewFinancials() || hasAnyRole(['InventoryViewer', 'InventorySensitiveViewer']);
  };

  const canViewSensitiveInventory = () => {
    return hasAnyRole(['InventorySensitiveViewer', 'SuperAdmin', 'SysAdmin']);
  };

  const canViewCompliance = () => {
    return hasAnyRole(['ComplianceUser', 'Compliance User']);
  };

  const value = {
    currentUser,
    loading,
    error,
    samlConfig,
    initiateLogin,
    handleSAMLCallback,
    manualLogin,
    logout,
    fetchCurrentUser,
    fetchSamlConfig,
    hasRole,
    hasAnyRole,
    isUser,
    isAdmin,
    isSysAdmin,
    isPowerUser,
    isSuperAdmin,
    canViewInventory,
    canViewFinancials,
    canViewSensitiveInventory,
    canViewCompliance,
    handleUnauthorized
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
